import styled, { css } from "styled-components/macro";
import {
  alertColor,
  lighterGrey,
  darkGrey,
  blueButton,
  medGrey,
  darkModeModalBackgroundColor,
  darkModeDarkTextColor,
} from "constants/colors";
import {
  FONT_SIZE_EXTRA_SMALL,
  FONT_SIZE_MEDIUM,
  FONT_SIZE_SMALL,
  FONT_SIZE_LARGE,
  NAV_HEIGHT_PX,
  Z_INDEX,
  BREAKPOINT_TABLET,
} from "constants/constants";
import { LOT_INFO_BAR_HEIGHT } from "./lotInfoUtils";

const ERROR_PADDING_BOTTOM = 14;
const ERROR_PADDING_BOTTOM_CSS = `
&:before {
  height: ${ERROR_PADDING_BOTTOM}px;
  bottom: -${ERROR_PADDING_BOTTOM}px;
}
`;

export const LotInfoBarStyles = styled.div<{
  $isDarkMode: boolean;
  $isError: boolean;
  $isPaused: boolean;
  $isEditMode: boolean;
  $hasCountExceededLotSize: boolean;
}>`
  position: relative;
  height: ${LOT_INFO_BAR_HEIGHT}px;
  /* if there's an error message, add padding bottom and subtract with negative margin to keep total height constant */
  &:before {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0px;
    height: 0px;
    background: ${({ $isDarkMode }) =>
      $isDarkMode ? darkModeModalBackgroundColor : lighterGrey};
    z-index: ${Z_INDEX.L2};
    transition: all 0.3s cubic-bezier(0.075, 0.82, 0.165, 1);
  }
  ${props => (props.$isError ? ERROR_PADDING_BOTTOM_CSS : "")}
  ${props =>
    props.$isPaused
      ? css`
          .row {
            opacity: 0.5;
          }
        `
      : ""}
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  background: ${({ $isDarkMode }) =>
    $isDarkMode ? darkModeModalBackgroundColor : lighterGrey};
  padding: 0.25em 0.75em 0.25em 0.5em;
  line-height: 1.4em;
  .btn .MuiIconButton-label {
    transform: scale(1.2);
  }
  .btnConfirm,
  .btnCancel {
    margin-left: 4px;
  }
  .MuiInputBase-input {
    padding: 4px 12px !important;
  }
  .defaultActions {
    display: flex;
    align-items: center;
  }
  .btnCompleteTheLotWrapper {
    &:hover {
      .btnCompleteLot {
        border: 1px solid ${medGrey};
      }
    }
  }
  .btnCompleteLot {
    transition: all 0.2s ease-out;
    border: 1px solid transparent;
    margin-left: 4px;
  }
  .MuiInputLabel-outlined.MuiInputLabel-shrink {
    width: 80px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    text-align: right;
  }
  .MuiInputLabel-outlined.MuiInputLabel-shrink {
    transform: translate(-64px, 1px) scale(0.75);
  }
  legend {
    display: none;
  }
  .PrivateNotchedOutline-root {
    top: 0;
  }
  .MuiFormHelperText-root {
    white-space: nowrap;
    z-index: ${Z_INDEX.L3};
    position: absolute;
    bottom: -${ERROR_PADDING_BOTTOM}px;
    margin: 0;
  }
  /* show help button on touch devices;
  on non-touch devices, show only on hover */
  &:not(.isTouchDevice) {
    [class*="HelpButtonStyles"] {
      display: none;
    }
    &:hover {
      [class*="HelpButtonStyles"] {
        display: block;
      }
    }
  }

  @media (max-width: ${BREAKPOINT_TABLET - 1}px) {
    flex-wrap: ${props => (props.$isEditMode ? "nowrap" : "wrap")};
    ${props =>
      props.$isEditMode
        ? css`
            align-items: flex-start;
          `
        : ""};
    align-content: flex-start;
    .btnsConfirmCancelWrapper {
      transform: scale(0.8);
      transform-origin: top;
    }
    ${props =>
      props.$hasCountExceededLotSize
        ? css`
            height: ${LOT_INFO_BAR_HEIGHT + 32}px;
          `
        : ""}
  }
`;

export const ForecastedLotCompletionTitleStyles = styled.div<{
  $isDarkMode: boolean;
}>`
  position: relative;
  .hoursAgo {
    position: absolute;
    white-space: nowrap;
    color: ${({ $isDarkMode }) =>
      $isDarkMode ? darkModeDarkTextColor : darkGrey};
    font-size: 0.65em;
    margin-left: 0.25em;
    top: 0;
    height: 24px;
    display: flex;
    align-items: center;
    left: 100%;
  }
`;

export const ThreeRowsStyles = styled.div<{
  $isDarkMode: boolean;
  $isEditMode: boolean;
  $width: number;
}>`
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  padding: ${props => (props.$isEditMode ? "4px 0 0 60px" : "0 60px")};
  .textField {
    width: 100px;
    margin: 0.25em 0;
  }
  .row-top {
    font-size: ${FONT_SIZE_EXTRA_SMALL}px;
  }
  .row-middle {
    font-size: ${FONT_SIZE_MEDIUM}px;
    font-weight: bold;
  }
  .row-bottom {
    font-size: ${FONT_SIZE_EXTRA_SMALL}px;
    color: ${({ $isDarkMode }) =>
      $isDarkMode ? darkModeDarkTextColor : darkGrey};
  }
  label[data-shrink="false"] {
    transform: translate(14px, 4px) scale(1);
  }
  @media (max-width: ${BREAKPOINT_TABLET - 1}px) {
    padding: ${props => (props.$isEditMode ? "4px 0 0 60px" : "0 24px")};
  }
`;

export const TwoRowsStyles = styled.div<{ $isDarkMode: boolean }>`
  display: flex;
  align-items: center;
  flex-direction: column;
  height: 100%;
  flex-grow: 1;
  .row-top {
    font-size: ${FONT_SIZE_SMALL}px;
  }
  .row-bottom {
    font-size: ${FONT_SIZE_LARGE}px;
    color: ${({ $isDarkMode }) =>
      $isDarkMode ? darkModeDarkTextColor : darkGrey};
    font-weight: bold;
    padding: 8px 0;
    &.small {
      font-size: ${FONT_SIZE_MEDIUM}px;
    }
  }
`;

export const CompleteTheLotDialogStyles = styled.div<{ $isDarkMode: boolean }>`
  width: 100%;
  display: flex;
  justify-content: center;
  margin-top: ${NAV_HEIGHT_PX}px;
  .content {
    border-radius: 4px;
    padding: 1.5em;
    width: 340px;
    background: white;
    .confirmText {
      line-height: 1.2em;
      .title {
        font-size: ${FONT_SIZE_SMALL}px;
        padding-bottom: 1em;
      }
      .subtext {
        font-size: ${FONT_SIZE_EXTRA_SMALL}px;
        color: ${({ $isDarkMode }) =>
          $isDarkMode ? darkModeDarkTextColor : darkGrey};
      }
      padding-bottom: 1.5em;
    }
    .actions {
      height: 42px;
      display: flex;
      justify-content: flex-end;
      button {
        margin-left: 1em;
      }
      .btnComplete {
        background: ${blueButton};
        color: white;
      }
    }
  }
`;

const EXCEEDED_BANNER_HEIGHT = 32;
export const HasExceededLotSizeWarningBannerStyles = styled.div`
  background: ${alertColor};
  position: absolute;
  bottom: -${EXCEEDED_BANNER_HEIGHT}px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  z-index: 7;
  .bannerContent {
    display: flex;
    justify-content: center;
    align-items: center;
    height: ${EXCEEDED_BANNER_HEIGHT}px;
    color: white;
  }
  .MuiSvgIcon-root {
    padding: 0 4px;
  }
  @media (max-width: ${BREAKPOINT_TABLET - 1}px) {
    bottom: 0px;
    font-size: 12px;
    line-height: 0.8em;
  }
`;
